import React, { useEffect, useRef, useState } from 'react';
import Chart from 'chart.js/auto';
import AuthServices from '../services/AuthServices';
import '../styles/StatsPage.css';

const REPORT_TOKEN = 'secret123';

function StatsPage() {
  const canvasRef = useRef(null); // référence vers le canvas HTML
  const chartInstanceRef = useRef(null); // référence vers l'objet Chart.js
  const [stats, setStats] = useState([]);
  const [report, setReport] = useState(null);
  const [reportError, setReportError] = useState(null);

  const renderDelta = (difference) => {
    if (difference === null || difference === undefined) {
      return null;
    }
    const diff = Number(difference);
    if (!Number.isFinite(diff)) {
      return null;
    }
    if (diff > 0) {
      return <span className="stats-delta stats-delta--positive">+{diff}</span>;
    }
    if (diff < 0) {
      return <span className="stats-delta stats-delta--negative">-{Math.abs(diff)}</span>;
    }
    return <span className="stats-delta stats-delta--neutral">0</span>;
  };

  useEffect(() => {
    let isCancelled = false;
    const controller = new AbortController();

    const loadStats = async () => {
      try {
        const v_key = AuthServices.getConnectedKey();
        const timestamp = Date.now();
        const [dailyResponse, reportResponse] = await Promise.all([
          fetch(`https://app.listaki.fr/api/WS/getDailyStats.php?key=${v_key}&t=${timestamp}`, {
            cache: 'no-store',
            signal: controller.signal,
          }),
          fetch(`https://app.listaki.fr/api/WS/getStatReport.php?token=${REPORT_TOKEN}&t=${timestamp}`, {
            cache: 'no-store',
            signal: controller.signal,
          }),
        ]);

        if (!dailyResponse.ok) {
          throw new Error('Erreur lors du chargement des statistiques quotidiennes');
        }
        if (!reportResponse.ok) {
          throw new Error('Erreur lors du chargement du rapport');
        }

        const [dailyData, reportData] = await Promise.all([dailyResponse.json(), reportResponse.json()]);

        if (isCancelled) {
          return;
        }

        setStats(Array.isArray(dailyData) ? dailyData : []);
        setReport(reportData);
        setReportError(null);
      } catch (err) {
        if (err.name === 'AbortError') {
          return;
        }
        console.error('Erreur chargement des stats', err);
        if (!isCancelled) {
          setReportError(err.message || 'Erreur lors du chargement des donnees');
        }
      }
    };

    loadStats();

    return () => {
      isCancelled = true;
      controller.abort();
    };
  }, []);

  useEffect(() => {
    if (stats.length === 0 || !canvasRef.current) return;

    // Nettoyage : détruire l’ancien graphique s’il existe
    if (chartInstanceRef.current) {
      chartInstanceRef.current.destroy();
    }

    const dates = stats.map(s => s.date.substring(5)); // Garder seulement MM-DD
    const users = stats.map(s => s.nb_users);
    const lists = stats.map(s => s.nb_lists);
    const objects = stats.map(s => s.nb_objects);
    const reservs = stats.map(s => s.nb_reserv);

    const ctx = canvasRef.current.getContext("2d");

    chartInstanceRef.current = new Chart(ctx, {
      type: 'line',
      data: {
        labels: dates,
        datasets: [
          {
            label: "Utilisateurs",
            data: users,
            borderColor: 'blue',
            fill: false,
          },
          {
            label: "Listes",
            data: lists,
            borderColor: 'green',
            fill: false,
          },
          {
            label: "Objets",
            data: objects,
            borderColor: 'orange',
            fill: false,
          },
          {
            label: "Reservations",
            data: reservs,
            borderColor: 'yellow',
            fill: false,
          },
        ],
      },
      options: {
        responsive: true,
        plugins: {
          legend: {
            position: 'top',
          },
        },
      },
    });

    // Cleanup automatique au démontage du composant
    return () => {
      if (chartInstanceRef.current) {
        chartInstanceRef.current.destroy();
      }
    };
  }, [stats]);

  return (
    <div className="stats-page">
      <div className="stats-container">
        <button
          className="back-button"
          onClick={() => (window.location.href = "/")}
        >
          ← Retour à l'application
        </button>

        <h2>Statistiques quotidiennes</h2>

        {reportError && (
          <div className="stats-report-error">
            {reportError}
          </div>
        )}

        {report && !reportError && (
          <div className="stats-report">
            <div className="stats-summary">
              <div className="stats-card">
                <span className="stats-value">{report.nb_users_use_today}</span>
                {renderDelta((report.nb_users_use_today ?? 0) - (report.nb_users_use_today ?? 0))}
                <span className="stats-label">Utilisateurs actifs aujourd'hui</span>
              </div>
              <div className="stats-card">
                <span className="stats-value">{report.nb_users}</span>
                {renderDelta((report.nb_users ?? 0) - (report.nb_users_yesterday ?? 0))}
                <span className="stats-label">Utilisateurs total</span>
              </div>
              <div className="stats-card">
                <span className="stats-value">{report.nb_lists}</span>
                {renderDelta((report.nb_lists ?? 0) - (report.nb_lists_yesterday ?? 0))}
                <span className="stats-label">Listes total</span>
              </div>
              <div className="stats-card">
                <span className="stats-value">{report.nb_objects}</span>
                {renderDelta((report.nb_objects ?? 0) - (report.nb_objects_yesterday ?? 0))}
                <span className="stats-label">Objets total</span>
              </div>
              <div className="stats-card">
                <span className="stats-value">{report.nb_reserv}</span>
                {renderDelta((report.nb_reserv ?? 0) - (report.nb_reserv_yesterday ?? 0))}
                <span className="stats-label">Reservations total</span>
              </div>
            </div>
          </div>
        )}

        <div className="stats-chart">
          <canvas ref={canvasRef}></canvas>
        </div>
        <p>
          <a
            href="https://umami.is/"
            target="_blank"
            rel="noopener noreferrer"
            className="stats-link"
          >
            Voir plus sur Umami
          </a>
        </p>
      </div>
    </div>

  );
}

export default StatsPage;
